"use client";

import type { AnalysisTarget } from "@/lib/ai/analysis-target";
import { buildAnalysisTargetNavigationPath } from "@/lib/ai/analysis-target-navigation";
import type { CurrentPageKind } from "@/lib/ai/current-page-context";

export type AnalysisTargetPathHint = {
  kind: "project" | "detail";
  projectType: AnalysisTarget["projectType"];
  projectId: string;
  viewId?: string;
  iwId?: string;
};

export type CurrentPagePathContext = {
  pageKind: CurrentPageKind;
  targetHint: AnalysisTargetPathHint | null;
  targetSyncKey: string | null;
};

type AnalysisTargetCandidate = {
  group: "project" | "view" | "index-worker";
  target: AnalysisTarget;
};

const ANALYSIS_TARGET_PROJECT_TYPE_BY_CATEGORY = {
  iw: "IndexWorkerProject",
  sa: "SearchAnalyticsProject",
  ua: "UserAnalyticsProject",
} as const satisfies Record<"iw" | "sa" | "ua", AnalysisTarget["projectType"]>;
const ANALYSIS_TARGET_DETAIL_KEY_BY_CATEGORY = {
  iw: "iwId",
  sa: "viewId",
  ua: "viewId",
} as const satisfies Record<"iw" | "sa" | "ua", "iwId" | "viewId">;
const PROJECT_TOP_SEGMENTS = new Set(["board", "list"]);
const PROJECT_OVERALL_SEGMENTS = new Set(["default"]);
const PROJECT_EXCLUDED_SEGMENTS = new Set(["settings", "session_explorer"]);
const ANALYSIS_TARGET_CATEGORY_SEGMENTS = new Set(["iw", "sa", "ua"] as const);
const ANALYSIS_TARGET_CATEGORY_INDEX_CANDIDATES = [2, 1] as const;
const BASE64_CANONICAL_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;
type AnalysisTargetCategorySegment = "iw" | "sa" | "ua";

function isAnalysisTargetCategorySegment(
  value: string,
): value is AnalysisTargetCategorySegment {
  return ANALYSIS_TARGET_CATEGORY_SEGMENTS.has(
    value as AnalysisTargetCategorySegment,
  );
}

/**
 * URL 由来 ID を decode 正規化し、同値比較しやすい文字列へ揃える。
 *
 * 多段エンコード（`%253D` -> `%3D` -> `=`）を吸収するため、値が安定するまで decode し続ける。
 * 不正な `%` シーケンスに遭遇した場合は、その時点の値で打ち切って返す。
 */
function normalizeAnalysisTargetId(id: string): string {
  let normalized = id;
  while (normalized.includes("%")) {
    try {
      const decoded = decodeURIComponent(normalized);
      if (decoded === normalized) {
        break;
      }
      normalized = decoded;
    } catch {
      break;
    }
  }
  return normalized;
}

/**
 * base64 / base64url の表記ゆれを同一比較用に正規化する。
 *
 * 契約:
 * - `-` / `_` は `+` / `/` へ寄せる（base64url -> base64）
 * - 末尾 padding は補完したうえで decode/encode し、正規形へそろえる
 * - 変換不可能な値は `null` を返し、呼び出し元で通常文字列比較へフォールバックする
 */
function normalizeBase64Variant(id: string): string | null {
  if (id.length === 0) {
    return null;
  }

  const base64Candidate = id.replace(/-/g, "+").replace(/_/g, "/");
  if (!BASE64_CANONICAL_PATTERN.test(base64Candidate)) {
    return null;
  }

  const remainder = base64Candidate.length % 4;
  if (remainder === 1) {
    return null;
  }

  const paddedBase64 =
    base64Candidate + "=".repeat(remainder === 0 ? 0 : 4 - remainder);
  if (
    typeof globalThis.atob !== "function" ||
    typeof globalThis.btoa !== "function"
  ) {
    return null;
  }

  try {
    return globalThis.btoa(globalThis.atob(paddedBase64));
  } catch {
    return null;
  }
}

/**
 * 比較・同期キー生成用の ID 正規化を返す。
 *
 * base64/base64url の両形式がありえる ID は canonical base64 へ寄せ、
 * それ以外の ID は通常の decode 正規化結果をそのまま返す。
 */
function normalizeAnalysisTargetComparableId(id: string): string {
  const normalized = normalizeAnalysisTargetId(id);
  return normalizeBase64Variant(normalized) ?? normalized;
}

/**
 * detail 名 lookup（`node(id)`）で使う ID を正規化する。
 *
 * 契約:
 * - `%3D` などの URL エンコード差分を吸収する
 * - base64url/base64 の表記ゆれがある場合は canonical base64 へ寄せる
 */
export function normalizeAnalysisTargetLookupId(id: string): string {
  return normalizeAnalysisTargetComparableId(id);
}

/**
 * detail 名 lookup 用に問い合わせる ID 候補を返す。
 *
 * 契約:
 * - 1件目は URL 由来の decode 正規化 ID（既存挙動を維持）
 * - 2件目は canonical base64 へ寄せた ID（表記ゆれ補正）
 * - 同値な場合は重複を除去して 1 件だけ返す
 */
export function getAnalysisTargetLookupIdCandidates(id: string): string[] {
  const normalizedId = normalizeAnalysisTargetId(id);
  const lookupId = normalizeAnalysisTargetLookupId(id);
  return normalizedId === lookupId ? [normalizedId] : [normalizedId, lookupId];
}

/**
 * 表記ゆれ（多段 decode / base64url）を吸収して ID 同値判定する。
 *
 * 契約:
 * - 文字列が完全一致しなくても、同一 ID なら `true`
 * - 表示ラベルが「IDフォールバックか」を判定する用途でも使える
 */
export function isSameAnalysisTargetId(left: string, right: string): boolean {
  return (
    normalizeAnalysisTargetComparableId(left) ===
    normalizeAnalysisTargetComparableId(right)
  );
}

/**
 * ラベルが ID フォールバックかを判定するための軽量同値比較。
 *
 * 契約:
 * - `%3D` と `=` のような URL エンコード差分のみ吸収する
 * - base64url / base64 の寄せは行わない（表示名の誤判定を避けるため）
 */
export function isSameAnalysisTargetFallbackId(
  left: string,
  right: string,
): boolean {
  return normalizeAnalysisTargetId(left) === normalizeAnalysisTargetId(right);
}

/**
 * URL パスのセグメントを path 同期用 ID と同じ規則で正規化する。
 *
 * `decodeURIComponent` の段数差分（`%3D` と `%253D` など）を吸収し、
 * URL 由来 hint と options 由来 ID の比較結果を安定させる。
 */
function normalizeAnalysisTargetPathSegment(segment: string): string {
  return normalizeAnalysisTargetId(segment);
}

/**
 * path 同期で使う detail 単位の正規化キーを返す。
 *
 * URL 由来 ID は decode 済み、options 由来 ID は encode 済みの場合がありえるため、
 * ここでは decode 正規化した ID で同一性を判定する。
 */
export function getAnalysisTargetPathSyncKey(
  target: AnalysisTarget | null,
): string | null {
  if (!target) {
    return null;
  }

  const normalizedProjectId = normalizeAnalysisTargetComparableId(
    target.projectId,
  );
  if (target.iwId) {
    return `${target.projectType}|${normalizedProjectId}|iw|${normalizeAnalysisTargetComparableId(target.iwId)}`;
  }
  if (target.viewId) {
    return `${target.projectType}|${normalizedProjectId}|view|${normalizeAnalysisTargetComparableId(target.viewId)}`;
  }
  return `${target.projectType}|${normalizedProjectId}|project`;
}

/**
 * URL から抽出した hint を、同期判定用の安定キーへ変換する。
 *
 * `resolve` 前でも path 変化を検知できるよう、hint 自体を正規化して使う。
 */
export function getAnalysisTargetPathHintSyncKey(
  hint: AnalysisTargetPathHint | null,
): string | null {
  if (!hint) {
    return null;
  }

  const normalizedProjectId = normalizeAnalysisTargetComparableId(
    hint.projectId,
  );
  if (hint.kind === "detail" && hint.iwId) {
    return `${hint.projectType}|${normalizedProjectId}|iw|${normalizeAnalysisTargetComparableId(hint.iwId)}`;
  }
  if (hint.kind === "detail" && hint.viewId) {
    return `${hint.projectType}|${normalizedProjectId}|view|${normalizeAnalysisTargetComparableId(hint.viewId)}`;
  }
  return `${hint.projectType}|${normalizedProjectId}|project`;
}

function findProjectCandidate(
  hint: Pick<AnalysisTargetPathHint, "projectType" | "projectId">,
  candidates: AnalysisTargetCandidate[],
): AnalysisTargetCandidate | undefined {
  return candidates.find(
    (candidate) =>
      candidate.group === "project" &&
      candidate.target.projectType === hint.projectType &&
      isSameAnalysisTargetId(candidate.target.projectId, hint.projectId),
  );
}

function safeDecodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function isProjectLevelSegment(
  category: AnalysisTargetCategorySegment,
  detailSegment: string,
): boolean {
  if (PROJECT_TOP_SEGMENTS.has(detailSegment)) {
    return true;
  }
  if (category !== "iw" && PROJECT_OVERALL_SEGMENTS.has(detailSegment)) {
    return true;
  }
  return false;
}

function buildAnalysisTargetPathHintFromLocation(
  pathLocation: AnalysisTargetPathLocation,
): AnalysisTargetPathHint | null {
  const { category, projectId, detailSegment } = pathLocation;
  const projectType = ANALYSIS_TARGET_PROJECT_TYPE_BY_CATEGORY[category];
  if (!detailSegment || isProjectLevelSegment(category, detailSegment)) {
    return {
      kind: "project",
      projectType,
      projectId,
    };
  }
  if (PROJECT_EXCLUDED_SEGMENTS.has(detailSegment)) {
    return null;
  }

  const detailKey = ANALYSIS_TARGET_DETAIL_KEY_BY_CATEGORY[category];
  if (detailKey === "iwId") {
    return {
      kind: "detail",
      projectType,
      projectId,
      iwId: detailSegment,
    };
  }
  return {
    kind: "detail",
    projectType,
    projectId,
    viewId: detailSegment,
  };
}

function buildProjectPathHint(
  category: AnalysisTargetCategorySegment,
  projectId: string,
): AnalysisTargetPathHint {
  return {
    kind: "project",
    projectType: ANALYSIS_TARGET_PROJECT_TYPE_BY_CATEGORY[category],
    projectId,
  };
}

function buildCurrentPagePathContextFromLocation(
  pathLocation: AnalysisTargetPathLocation,
): CurrentPagePathContext | null {
  const { category, projectId, detailSegment } = pathLocation;
  const projectHint = buildProjectPathHint(category, projectId);

  if (category === "iw") {
    if (!detailSegment || detailSegment === "board") {
      return {
        pageKind: "iw_project_board",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }
    if (detailSegment === "list") {
      return {
        pageKind: "iw_project_list",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }
    if (detailSegment === "settings") {
      return {
        pageKind: "iw_project_settings",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }

    const detailHint = {
      kind: "detail",
      projectType: projectHint.projectType,
      projectId,
      iwId: detailSegment,
    } satisfies AnalysisTargetPathHint;
    return {
      pageKind: "iw_detail",
      targetHint: detailHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(detailHint),
    };
  }

  if (category === "sa") {
    if (!detailSegment || detailSegment === "default") {
      return {
        pageKind: "sa_project_default",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }
    if (detailSegment === "board") {
      return {
        pageKind: "sa_project_board",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }
    if (detailSegment === "list") {
      return {
        pageKind: "sa_project_list",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }
    if (detailSegment === "settings") {
      return {
        pageKind: "sa_project_settings",
        targetHint: projectHint,
        targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
      };
    }

    const detailHint = {
      kind: "detail",
      projectType: projectHint.projectType,
      projectId,
      viewId: detailSegment,
    } satisfies AnalysisTargetPathHint;
    return {
      pageKind: "sa_view_detail",
      targetHint: detailHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(detailHint),
    };
  }

  if (!detailSegment || detailSegment === "default") {
    return {
      pageKind: "ua_project_default",
      targetHint: projectHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
    };
  }
  if (detailSegment === "board") {
    return {
      pageKind: "ua_project_board",
      targetHint: projectHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
    };
  }
  if (detailSegment === "list") {
    return {
      pageKind: "ua_project_list",
      targetHint: projectHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
    };
  }
  if (detailSegment === "settings") {
    return {
      pageKind: "ua_project_settings",
      targetHint: projectHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
    };
  }
  if (detailSegment === "session_explorer") {
    return {
      pageKind: "ua_session_explorer",
      targetHint: projectHint,
      targetSyncKey: getAnalysisTargetPathHintSyncKey(projectHint),
    };
  }

  const detailHint = {
    kind: "detail",
    projectType: projectHint.projectType,
    projectId,
    viewId: detailSegment,
  } satisfies AnalysisTargetPathHint;
  return {
    pageKind: "ua_view_detail",
    targetHint: detailHint,
    targetSyncKey: getAnalysisTargetPathHintSyncKey(detailHint),
  };
}

type AnalysisTargetPathLocation = {
  category: "iw" | "sa" | "ua";
  projectId: string;
  detailSegment?: string;
};

/**
 * `pathname` から `category/project/detail` の位置を特定する。
 *
 * 固定インデックス（`[2]`）には依存せず、以下の両方を許容する:
 * - `/:lang/:workspaceId/:category/:projectId/:detail?`
 * - `/:workspaceId/:category/:projectId/:detail?`
 */
function findAnalysisTargetPathLocation(
  normalizedSegments: string[],
): AnalysisTargetPathLocation | null {
  for (const categoryIndex of ANALYSIS_TARGET_CATEGORY_INDEX_CANDIDATES) {
    const category = normalizedSegments[categoryIndex];
    if (!category) {
      continue;
    }

    if (category === "ai") {
      return null;
    }
    if (!isAnalysisTargetCategorySegment(category)) {
      continue;
    }

    const projectId = normalizedSegments[categoryIndex + 1];
    if (!projectId) {
      return null;
    }
    // 実行計画はワークスペース全体の固定ページで、projectId を持たない。
    if (category === "iw" && projectId === "job-plan") {
      return null;
    }

    return {
      category,
      projectId,
      detailSegment: normalizedSegments[categoryIndex + 2],
    };
  }
  return null;
}

type AnalysisTargetNavigationContext = {
  lang: string;
  workspaceId: string;
};

/**
 * 現在パスから言語・workspace を抽出する。
 *
 * 契約:
 * - `pathname` を唯一のソースとして扱う（`params` 由来値は使わない）
 * - 先頭 2 セグメント（`/:lang/:workspaceId/...`）が揃っていない場合は `null`
 */
function getAnalysisTargetNavigationContext(
  pathname: string,
): AnalysisTargetNavigationContext | null {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);

  const lang = safeDecodePathSegment(segments[0] ?? "");
  const workspaceId = normalizeAnalysisTargetPathSegment(segments[1] ?? "");
  if (!lang || !workspaceId) {
    return null;
  }

  return {
    lang,
    workspaceId,
  };
}
/**
 * 現在の URL から、分析対象の候補キー（project/view id）を抽出する。
 *
 * 契約:
 * - `pathname` を単一ソースとして、以下の両方を解釈する
 *   - `/:lang/:workspaceId/:category/:projectId/:detail?`
 *   - `/:workspaceId/:category/:projectId/:detail?`
 * - `/ai` 配下は初期選択対象を持たない
 * - project コンテキストは `/board` / `/list` のみ
 * - detail コンテキストは `iw/:iwId` / `sa|ua/:viewId`
 * - `sa|ua/:projectId/default` は全体画面として project コンテキストを返す
 * - `settings` / `session_explorer` など対象外画面は `null`
 */
export function getAnalysisTargetPathHint(
  pathname: string,
): AnalysisTargetPathHint | null {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  const normalizedSegments = segments.map(normalizeAnalysisTargetPathSegment);
  const pathLocation = findAnalysisTargetPathLocation(normalizedSegments);
  if (!pathLocation) {
    return null;
  }
  return buildAnalysisTargetPathHintFromLocation(pathLocation);
}

/**
 * 現在表示中のページ種別と、それに紐づく target hint を返す。
 *
 * `analysisScope.pageRefId` 用の hint と違って、settings / session explorer のような
 * 非分析対象ページでも「今どこにいるか」を伝えるため project context を返す。
 */
export function getCurrentPagePathContext(
  pathname: string,
): CurrentPagePathContext | null {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  const normalizedSegments = segments.map(normalizeAnalysisTargetPathSegment);
  const pathLocation = findAnalysisTargetPathLocation(normalizedSegments);
  if (!pathLocation) {
    return null;
  }
  return buildCurrentPagePathContextFromLocation(pathLocation);
}

type AnalysisTargetDetailMatchConfig = {
  group: AnalysisTargetCandidate["group"];
  detailId: string;
  getDetailId: (target: AnalysisTarget) => string | undefined;
};

function getAnalysisTargetDetailMatchConfig(
  hint: AnalysisTargetPathHint,
): AnalysisTargetDetailMatchConfig | null {
  if (hint.kind !== "detail") {
    return null;
  }
  if (hint.iwId) {
    return {
      group: "index-worker",
      detailId: hint.iwId,
      getDetailId: (target) => target.iwId,
    };
  }
  if (hint.viewId) {
    return {
      group: "view",
      detailId: hint.viewId,
      getDetailId: (target) => target.viewId,
    };
  }
  return null;
}

/**
 * URL 由来の候補キーを実データ候補に解決する。
 *
 * 契約:
 * - detail は projectId + detailId の両方一致でのみ採用する（他 project の同一 view/iw にはフォールバックしない）
 * - 候補が見つからない場合は `null` を返す（固定ラベルやID文字列は返さない）
 */
export function resolveAnalysisTargetFromPathHint(
  hint: AnalysisTargetPathHint,
  candidates: AnalysisTargetCandidate[],
): AnalysisTarget | null {
  const detailMatchConfig = getAnalysisTargetDetailMatchConfig(hint);
  if (detailMatchConfig) {
    return (
      candidates.find((candidate) => {
        const detailId = detailMatchConfig.getDetailId(candidate.target);
        if (!detailId) {
          return false;
        }
        return (
          candidate.group === detailMatchConfig.group &&
          candidate.target.projectType === hint.projectType &&
          isSameAnalysisTargetId(candidate.target.projectId, hint.projectId) &&
          isSameAnalysisTargetId(detailId, detailMatchConfig.detailId)
        );
      })?.target ?? null
    );
  }
  if (hint.kind !== "project") {
    return null;
  }

  const matchedProject = findProjectCandidate(hint, candidates);
  return matchedProject?.target ?? null;
}

/**
 * 分析対象ターゲットを開くためのページURLを生成する。
 *
 * - IW detail: `/:lang/:workspaceId/iw/:projectId/:iwId`
 * - IW project: `/:lang/:workspaceId/iw/:projectId`
 * - SA/UA: view 指定時は `/:lang/:workspaceId/{sa|ua}/:projectId/:viewId`
 * - SA/UA: view 未指定時は project ルートへ遷移（サーバー側の既存リダイレクトに委譲）
 */
export function buildAnalysisTargetHref(
  target: AnalysisTarget,
  pathname: string,
): string | null {
  const navigationContext = getAnalysisTargetNavigationContext(pathname);
  if (!navigationContext) {
    return null;
  }

  return buildAnalysisTargetNavigationPath(target, navigationContext);
}
